import React, { useState, useEffect, useMemo } from "react";
import JSONDisplay from "./JSONDisplay";
import ImageDisplay from "./ImageDisplay";
import {
  getCachedPageDataWithBbox,
  getCachedPageImage,
} from "../../../utils/pageCache";
import { transformToLegacyFormat } from "../../../utils/transformData";

type DisplayPredictionsProps = {
  documentID: string;
  pageID: string | null;
  mode?: "image-only" | "json-only" | "both";
  searchQuery?: string;
  highlightDuration?: number;
  isCurrentSearchResult?: boolean;
  currentOccurrenceIndex?: number | undefined;
  selectedTerm?: string;
  onTermSelect?: (term: string) => void;
  onHoverObject?: (uuid: string | null) => void;
  highlightedObjectUuid?: string | null | undefined;
  highlightedBbox?: number[] | null;
  bboxVisibility?: { text: boolean; molecules: boolean; tables: boolean } | undefined;
};

type PageData = {
  raw_data: any;
  readable_data: any;
};

const DisplayPredictions: React.FC<DisplayPredictionsProps> = ({
  documentID,
  pageID,
  mode = "both",
  searchQuery,
  highlightDuration,
  isCurrentSearchResult = false,
  currentOccurrenceIndex,
  selectedTerm,
  onTermSelect,
  onHoverObject,
  highlightedObjectUuid,
  highlightedBbox,
  bboxVisibility,
}) => {
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [imageSrc, setImageSrc] = useState<string>("");
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [jsonLoading, setJsonLoading] = useState<boolean>(true);
  const [rawData, setRawData] = useState<any>({});
  const [readableData, setReadableData] = useState<any>({});

  useEffect(() => {
    if (!imageSrc) {
      setLoadedImage(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      setLoadedImage(img);
      setImageLoading(false);
    };
    img.onerror = () => setImageLoading(false);
    img.src = imageSrc;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageSrc]);

  useEffect(() => {
    if (!pageID) return;

    let cancelled = false;

    const fetchImage = async () => {
      try {
        const { data } = await getCachedPageImage(documentID, pageID);
        if (!cancelled) {
          setImageSrc(data.image);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching image:", error);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
    };
  }, [documentID, pageID]);

  useEffect(() => {
    if (!pageID) return;

    let cancelled = false;

    const fetchJson = async () => {
      try {
        const { status, data }: { status: number; data: PageData } =
          await getCachedPageDataWithBbox(documentID, pageID);
        if (!cancelled) {
          if (status === 206) {
            console.info("Results not ready...");
            setRawData({});
            setReadableData({});
          } else {
            setRawData(transformToLegacyFormat(data.raw_data));
            setReadableData(data.readable_data);
          }
          setJsonLoading(false);
        }
      } catch (error: any) {
        if (!cancelled) {
          console.error(`Error: ${error.message}`);
          setJsonLoading(false);
        }
      }
    };

    fetchJson();

    return () => {
      cancelled = true;
    };
  }, [documentID, pageID]);

  // Merge text_block objects from readable_data into rawData so ImageDisplay
  // can draw the green block outlines on top of the original detections.
  const rawDataWithBlocks = useMemo(() => {
    if (!rawData?.objects) return rawData;
    const rd = readableData as any;
    if (!rd?.objects || typeof rd.objects !== "object") return rawData;
    const textBlocks = Object.entries(rd.objects)
      .filter(([, obj]: any) => obj?.kind === "text_block" && obj?.bbox)
      .map(([uuid, obj]: any) => ({
        cls: "text_block" as const,
        bbox: obj.bbox,
        metadata: { block_kind: obj.metadata?.block_kind },
        uuid,
      }));
    if (textBlocks.length === 0) return rawData;
    return { ...rawData, objects: [...rawData.objects, ...textBlocks] };
  }, [rawData, readableData]);

  if (mode === "image-only") {
    return (
      <div className="h-full w-full">
        {imageLoading ? (
          <div className="h-full w-full bg-slate-200 animate-pulse rounded" />
        ) : (
          <ImageDisplay
            data={rawDataWithBlocks}
            image={loadedImage}
            {...(selectedTerm && { selectedTerm })}
            {...(onHoverObject && { onHoverObject })}
            highlightedObjectUuid={highlightedObjectUuid}
            {...(highlightedBbox !== undefined && highlightedBbox !== null && { highlightedBbox })}
            bboxVisibility={bboxVisibility}
          />
        )}
      </div>
    );
  }

  if (mode === "json-only") {
    return (
      <div className="h-full w-full p-4">
        {jsonLoading ? (
          <div className="space-y-3 animate-pulse">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="h-3 bg-slate-200 rounded"
                style={{ width: `${60 + (i * 17) % 35}%` }}
              />
            ))}
          </div>
        ) : (
          <JSONDisplay
            data={readableData}
            {...(searchQuery !== undefined && { searchQuery })}
            {...(highlightDuration !== undefined && { highlightDuration })}
            isCurrentSearchResult={isCurrentSearchResult}
            currentOccurrenceIndex={currentOccurrenceIndex}
            {...(onTermSelect && { onTermSelect })}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full w-full relative">
      <ImageDisplay
        data={rawDataWithBlocks}
        image={loadedImage}
        {...(selectedTerm && { selectedTerm })}
        {...(onHoverObject && { onHoverObject })}
      />
      <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-white/70 backdrop-blur-sm p-4 border-t border-slate-200 shadow-2xl">
        <JSONDisplay
          data={readableData}
          {...(searchQuery !== undefined && { searchQuery })}
          {...(highlightDuration !== undefined && { highlightDuration })}
          isCurrentSearchResult={isCurrentSearchResult}
          currentOccurrenceIndex={currentOccurrenceIndex}
          {...(onTermSelect && { onTermSelect })}
        />
      </div>
    </div>
  );
};

export default DisplayPredictions;
